import { Helmet } from 'react-helmet-async';
import { useParams } from 'react-router-dom';
// @mui
import { alpha } from '@mui/material/styles';
import { Grid, Button, Container, Stack, Typography, Backdrop, CircularProgress, Chip, Divider } from '@mui/material';
import { useState, useEffect } from 'react';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined';

import { GET_MODEL_IMAGES_URL } from '../utils/services';

// components
import { BlogPostCard } from '../sections/@dashboard/blog';
import { getCachedData, putCacheData } from '../utils/cacher';
import JuicyAdsComponent from '../components/ads/JuicyAdsComponent';

// ----------------------------------------------------------------------

export default function LoifPost() {
  const [DEFAULT_IMAGES_SHOWN, setDefaultImagesShown] = useState(29);

  const [images, setImages] = useState([]);
  const [videos, setVideos] = useState([]);
  const [nameId, setNameId] = useState('');
  const [isLoading, setLoading] = useState(true);
  const [imagesShowing, setImagesShowing] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [dateModified, setDateModified] = useState('Unknown');

  const { id } = useParams();

  useEffect(() => {
    const isVideo = (url) => url.endsWith('.mp4');

    const responseSuccessAction = (result) => {
      setImages(result.filter(item => !isVideo(item)));
      setVideos(result.filter(item => isVideo(item)));

      if (result.length > 300) {
        setDefaultImagesShown(100);
      }
    };

    const searchParams = new URLSearchParams(window.location.search);

    if (id === undefined || id === null || id === '' || id === 'index.html') {
      window.location.href = '/';
    }

    setNameId(id);
    const date = searchParams.get('date');
    setDateModified(date);

    setLoading(true);

    const url = GET_MODEL_IMAGES_URL(id, date);

    getCachedData(url).then((items) => {
      if (items) {
        responseSuccessAction(items);
        console.debug(`From cache ${url}`);
        setLoading(false);
      } else {
        fetch(url)
          .then(res => res.json())
          .then(
            (result) => {
              if (result !== undefined) {
                responseSuccessAction(result);
                putCacheData(url, result);
              }
              setLoading(false);
            },
            (error) => {
              console.error(error);
              setLoading(false);
            }
          );
      }
    });
  }, [id]);

  const handleNext = () => {
    const calc = imagesShowing + DEFAULT_IMAGES_SHOWN;
    if (calc < images.length) {
      setImagesShowing(calc);
      setCurrentPage(currentPage + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevious = () => {
    const calc = imagesShowing - DEFAULT_IMAGES_SHOWN;
    if (calc >= 0) {
      setImagesShowing(calc);
      setCurrentPage(currentPage - 1);
      window.scrollTo(0, 0);
    }
  };

  const totalPages = Math.ceil(images.length / DEFAULT_IMAGES_SHOWN);
  const hasNoImages = !isLoading && images.length === 0;

  return (
    <>
      <Helmet>
        <title> {nameId} collection of beautiful photos videos leaks | Loif </title>
      </Helmet>

      <Container sx={{ py: 4 }}>
        {/* Page header */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
          mb={4}
        >
          <Stack spacing={1}>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                background: hasNoImages
                  ? undefined
                  : (theme) => `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                WebkitBackgroundClip: hasNoImages ? undefined : 'text',
                WebkitTextFillColor: hasNoImages ? undefined : 'transparent',
                backgroundClip: hasNoImages ? undefined : 'text',
              }}
            >
              {hasNoImages ? `No collections found for: ${nameId}` : nameId}
            </Typography>
            {!hasNoImages && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  icon={<PhotoLibraryOutlinedIcon fontSize="small" />}
                  label={`${images.length} Photos`}
                  size="small"
                  variant="outlined"
                  sx={{ borderRadius: 2, fontWeight: 500 }}
                />
                <Chip
                  icon={<VideocamOutlinedIcon fontSize="small" />}
                  label={`${videos.length} Videos`}
                  size="small"
                  variant="outlined"
                  sx={{ borderRadius: 2, fontWeight: 500 }}
                />
              </Stack>
            )}
          </Stack>
          <Typography
            variant="caption"
            sx={{
              color: 'text.disabled',
              fontWeight: 500,
              whiteSpace: 'nowrap',
            }}
          >
            Last Updated: {(dateModified && dateModified !== 'null') ? dateModified : 'Unknown'}
          </Typography>
        </Stack>

        <JuicyAdsComponent />

        <Grid container spacing={2.5}>
          <JuicyAdsComponent adType="square" />

          {images
            .slice(imagesShowing, Math.min(imagesShowing + DEFAULT_IMAGES_SHOWN, images.length))
            .map((imageUrl, index) => (
              <BlogPostCard
                key={index}
                post={{
                  folder_name: nameId,
                  last_modified_date: dateModified,
                }}
                index={index}
                cover={imageUrl}
                isPost
              />
            ))}
        </Grid>

        {/* Image Pagination */}
        {images.length > 0 && (
          <Stack direction="row" alignItems="center" justifyContent="center" spacing={2} mt={5} mb={3}>
            <Button
              variant="outlined"
              startIcon={<NavigateBeforeIcon />}
              onClick={handlePrevious}
              disabled={currentPage <= 1}
              sx={{ minWidth: 44, px: 1.5 }}
            />
            <Chip
              label={`${currentPage} / ${totalPages}`}
              variant="outlined"
              sx={{
                fontWeight: 600,
                fontSize: '0.9rem',
                px: 1,
                borderRadius: 2,
                borderColor: (theme) => alpha(theme.palette.grey[500], 0.3),
              }}
            />
            <Button
              variant="outlined"
              endIcon={<NavigateNextIcon />}
              onClick={handleNext}
              disabled={currentPage >= totalPages}
              sx={{ minWidth: 44, px: 1.5 }}
            />
          </Stack>
        )}

        {/* Videos section */}
        {videos.length > 0 && (
          <>
            <Divider sx={{ mt: 6, mb: 4 }}>
              <Chip
                icon={<VideocamOutlinedIcon fontSize="small" />}
                label={`${videos.length} Videos`}
                variant="outlined"
                sx={{ fontWeight: 600, borderRadius: 2 }}
              />
            </Divider>

            <Grid container spacing={2.5}>
              <JuicyAdsComponent adType="square" />

              {videos.map((videoUrl, index) => (
                <BlogPostCard
                  key={index}
                  post={{
                    folder_name: nameId,
                    last_modified_date: dateModified,
                  }}
                  index={index}
                  cover={videoUrl}
                  isPost
                />
              ))}
            </Grid>
          </>
        )}
      </Container>

      <Backdrop
        sx={{
          color: '#fff',
          zIndex: (theme) => theme.zIndex.drawer + 1,
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(0,0,0,0.6)',
        }}
        open={isLoading}
      >
        <CircularProgress color="primary" />
      </Backdrop>
    </>
  );
}
